import type { ReactNode } from 'react';
import { Typography } from 'antd';

const { Title, Paragraph } = Typography;

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right-aligned actions — buttons, switchers, filters. */
  extra?: ReactNode;
  /**
   * Render as a plain heading instead of the gradient band. Use on nested or
   * detail screens where a second banner would compete with the page's own.
   */
  plain?: boolean;
}

/**
 * The banner every page opens with: title, optional subtitle, optional actions,
 * sitting on the brand gradient. Keeping it in one component is what stops the
 * sections from drifting apart visually — previously each page hand-rolled its
 * own `<h1>` and only the dashboard had the band.
 */
export function PageHeader({ title, subtitle, extra, plain }: PageHeaderProps) {
  const body = (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 24,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <Title level={3} style={{ margin: 0, fontWeight: 650, letterSpacing: '-0.015em' }}>
          {title}
        </Title>
        {subtitle ? (
          <Paragraph
            type="secondary"
            style={{ marginTop: 4, marginBottom: 0, maxWidth: '78ch', fontSize: 13.5 }}
          >
            {subtitle}
          </Paragraph>
        ) : null}
      </div>
      {extra ? <div style={{ flexShrink: 0 }}>{extra}</div> : null}
    </div>
  );

  if (plain) return <div style={{ marginBottom: 4 }}>{body}</div>;

  return (
    <section
      className="brand-gradient"
      style={{
        borderRadius: 18,
        padding: '24px 28px',
        border: '1px solid var(--ant-color-border-secondary)',
        boxShadow: 'var(--ant-box-shadow-tertiary)',
      }}
    >
      {body}
    </section>
  );
}
